import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { alphaApi } from '../../api/alpha';
import { authApi } from '../../api/auth';
import { brandApi } from '../../api/brand';
import { setScope } from '../../lib/auth-storage';

const STEPS = [
  'Welcome',
  'Verify Email',
  'Organization',
  'Brand',
  'Workspace',
  'Amazon OAuth',
  'Initial Sync',
  'Brand DNA',
  'Dashboard',
];

export function OnboardingWizardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialStep = searchParams.get('step') === 'verify' ? 1 : 0;
  const [step, setStep] = useState(initialStep);
  const [orgName, setOrgName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [brandId, setBrandId] = useState('');
  const [dnaSummary, setDnaSummary] = useState('');
  const [error, setError] = useState('');
  const [journeyId, setJourneyId] = useState('');

  async function startJourney() {
    try {
      const run = await alphaApi.startJourney('enterprise_onboarding');
      setJourneyId(run.id);
    } catch {
      /* alpha optional in dev */
    }
  }

  async function verifyEmail() {
    setError('');
    await authApi.verifyEmail(verifyToken);
    setStep(2);
  }

  async function createBrand() {
    setError('');
    const orgId = localStorage.getItem('aiecom_org_id') ?? import.meta.env.VITE_ORG_ID;
    if (!orgId) {
      setError('Organization context required before creating a brand.');
      return;
    }
    const brand = await brandApi.create({
      organization_id: orgId,
      name: brandName,
      slug: brandName.toLowerCase().replace(/\s+/g, '-'),
    });
    setBrandId(brand.id);
    const _t = localStorage.getItem('aiecom_tenant_id');
    const _o = localStorage.getItem('aiecom_org_id');
    if (_t && _o) setScope(_t, _o, brand.id);
    setStep(5);
  }

  async function buildDNA() {
    setError('');
    const dna = await brandApi.buildDNA(brandId, {
      store_title: brandName,
      brand_story: `${brandName} delivers premium marketplace products.`,
    });
    setDnaSummary(dna.brand_promise ?? dna.personality?.tone?.join(', ') ?? 'Generated');
    setStep(8);
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold text-slate-900">Enterprise Onboarding</h1>
        <p className="text-sm text-slate-600">IMP-00035 · {journeyId ? `Journey ${journeyId}` : 'Local wizard'}</p>
        <ol className="mt-6 flex flex-wrap gap-2">
          {STEPS.map((label, i) => (
            <li key={label} className={`rounded-full px-3 py-1 text-xs ${i === step ? 'bg-slate-900 text-white' : i < step ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'}`}>
              {label}
            </li>
          ))}
        </ol>
        {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          {step === 0 && (
            <>
              <p className="text-slate-700">Welcome to AI-COS. We will set up your organization, brand, workspace, and Amazon connection.</p>
              <button type="button" onClick={() => { startJourney(); setStep(1); }} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Get started</button>
            </>
          )}
          {step === 1 && (
            <>
              <label className="block text-sm font-medium">Verification token (dev)</label>
              <input value={verifyToken} onChange={(e) => setVerifyToken(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" />
              <button type="button" onClick={verifyEmail} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Verify email</button>
            </>
          )}
          {step === 2 && (
            <>
              <label className="block text-sm font-medium">Organization name</label>
              <input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" placeholder="Acme Commerce Pvt Ltd" />
              <button type="button" onClick={() => setStep(3)} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Continue</button>
            </>
          )}
          {step === 3 && (
            <>
              <label className="block text-sm font-medium">Brand name</label>
              <input value={brandName} onChange={(e) => setBrandName(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" placeholder="Nakabh" />
              <button type="button" onClick={() => setStep(4)} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Continue</button>
            </>
          )}
          {step === 4 && (
            <>
              <label className="block text-sm font-medium">Workspace name</label>
              <input value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" placeholder="Amazon India" />
              <button type="button" onClick={createBrand} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Create brand & continue</button>
            </>
          )}
          {step === 5 && (
            <>
              <p className="text-slate-700">Authorize Amazon via secure OAuth. No manual Client ID or refresh tokens required.</p>
              <a href="/amazon/connect" className="mt-4 inline-block rounded-lg bg-orange-600 px-4 py-2 text-white">Connect Amazon</a>
              <button type="button" onClick={() => setStep(6)} className="ml-2 mt-4 rounded-lg border px-4 py-2">Skip for now</button>
            </>
          )}
          {step === 6 && (
            <>
              <p className="text-slate-700">Select initial sync: Products, Inventory, Orders (default selected).</p>
              <button type="button" onClick={() => setStep(7)} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Run sync & continue</button>
            </>
          )}
          {step === 7 && (
            <>
              <p className="text-slate-700">Generate structured Brand DNA from your Amazon store signals.</p>
              <button type="button" onClick={buildDNA} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Generate Brand DNA</button>
            </>
          )}
          {step === 8 && (
            <>
              <p className="text-green-700">Onboarding complete. Brand DNA: {dnaSummary}</p>
              <button type="button" onClick={() => navigate('/dashboard')} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-white">Enter dashboard</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
